/**
 * Opening a DOCUMENT app makes one (RFC 0271 S4).
 *
 * The first version of this was a picker: recent records plus New. That was
 * application-shaped thinking surviving the flip — the sidebar already lists
 * every record and ⌘T already finds them, so a third list was duplication, and
 * only its New button had a reason to exist. Selecting the app IS New.
 *
 * So there is no chooser. Selecting a document app mints a record and opens it,
 * the way TextEdit opens a blank document rather than asking which one you
 * meant. (Pages shows a template chooser; the split tracks configuration cost,
 * and a record that needs configuring should ask for it in its own empty state,
 * not in a modal before it exists.)
 *
 * REUSE, not just minting, because otherwise clicking an app twice litters the
 * project with empty records that never get filled. If the newest record is
 * still untouched, that one is handed back. Repeated selections converge on a
 * single scratch record instead of accumulating.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  createDocument,
  type DocumentRef,
  readDocumentContent,
  readDocumentData,
} from '@mod/core/model'
import { useEffect, useRef, useState } from 'react'
import { personChangeMessage } from '../../services/identity/localPerson'
import { useDocument } from '../../services/world/repo/useDocument'

/** Newest first, so "the most recent record" has one meaning. */
export function newestFirst(records: DocumentRef[]): DocumentRef[] {
  return [...records].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
}

/**
 * Has anything been put in this record yet?
 *
 * Untouched means no fields and no body. A record with even one field was
 * worked on, and handing it back as a scratch pad would silently reopen someone
 * else's draft.
 */
export function isUntouched(doc: unknown): boolean {
  const data = readDocumentData(doc)
  const hasFields = !!data && Object.keys(data).length > 0
  return !hasFields && !readDocumentContent(doc)
}

function newRecordPath(dataType: string): string {
  return `${dataType}/${Math.random().toString(36).slice(2, 10)}`
}

/**
 * The one place a record of a document type comes into being.
 *
 * Shared because there are two doors — selecting the app, and "New X" in ⌘T —
 * and two doors with two implementations is how the reuse rule ends up applying
 * to one of them.
 *
 * Reuses the newest record when it is untouched, so repeated creation converges
 * on a single scratch record instead of littering. Only the newest is checked:
 * one hydration, and an older empty record is not something anyone is still
 * looking for.
 */
export async function openOrCreateInstance(
  repo: Repo,
  projectId: string,
  dataType: string,
  records: DocumentRef[],
): Promise<string> {
  const newest = newestFirst(records)[0]
  if (newest) {
    const handle = await repo.find(newest.id as AnyDocumentId)
    await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
    if (isUntouched(handle.doc())) return newest.id
  }
  const handle = await createDocument(repo, projectId, {
    ...personChangeMessage(),
    path: newRecordPath(dataType),
    mime: 'application/mod-instance',
    dataType,
    data: {},
  })
  return handle.documentId as string
}

export function NewInstanceGate({
  appName,
  dataType,
  projectId,
  records,
  onOpen,
}: {
  appName: string
  dataType: string
  projectId: string
  records: DocumentRef[]
  onOpen: (documentId: string) => void
}) {
  const repo = useRepo() as unknown as Repo
  const [failure, setFailure] = useState<string | null>(null)

  // Only the newest is checked for reuse: one hydration, and an older empty
  // record is not a scratch pad anyone is still looking for.
  const newest = newestFirst(records)[0]
  const [newestDoc] = useDocument(newest?.id as AnyDocumentId | undefined)

  // Once per mount. Without the ref this mints on every render, and React's
  // development double-invoke alone would create two records per click.
  const done = useRef(false)

  useEffect(() => {
    if (done.current) return
    // Wait for the reuse candidate to hydrate before deciding, or the first
    // decision is always "mint" and the reuse rule never fires.
    if (newest && newestDoc === undefined) return
    done.current = true

    if (newest && newestDoc !== undefined && isUntouched(newestDoc)) {
      onOpen(newest.id)
      return
    }
    void createDocument(repo, projectId, {
      ...personChangeMessage(),
      path: newRecordPath(dataType),
      mime: 'application/mod-instance',
      dataType,
      data: {},
    })
      .then((handle) => onOpen(handle.documentId as string))
      .catch((e) => {
        done.current = false
        setFailure(e instanceof Error ? e.message : String(e))
      })
  }, [repo, projectId, dataType, newest, newestDoc, onOpen])

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-6">
      {failure ? (
        <p className="max-w-sm text-center text-destructive text-sm">
          Could not create a {appName} record: {failure}
        </p>
      ) : (
        <p className="text-muted-foreground/60 text-sm">Opening {appName}…</p>
      )}
    </div>
  )
}
